/**
 * RiskLevelIndicator — inherent-risk channel chip (Lucide-style icon, no emoji).
 *
 * Shows ONLY inherent risk/impact (the "how dangerous is this operation"
 * channel). It deliberately does NOT encode governance coverage — that is the
 * separate reassurance channel carried by GovernanceSignal / CdaGateLadder.
 */

import type { CdaRiskLevel } from './types';
import { RISK_COLOR, RISK_LABEL, softFill } from './colors';
import { CdaIcon } from './icons';
import type { CdaIconName } from './icons';

const React = (globalThis as any).React;

export interface RiskLevelIndicatorProps {
  level: CdaRiskLevel;
  /** Raw declared risk string, when it differs from the gating level (e.g. "write"). */
  rawLabel?: string;
  size?: 'sm' | 'md';
  className?: string;
}

const ICON: Record<CdaRiskLevel, CdaIconName> = {
  read: 'eye',
  low: 'shield',
  medium: 'alert-triangle',
  high: 'shield-alert',
  critical: 'shield-alert',
};

export function RiskLevelIndicator({ level, rawLabel, size = 'md', className }: RiskLevelIndicatorProps) {
  const color = RISK_COLOR[level];
  const label = rawLabel || RISK_LABEL[level];
  const fontSize = size === 'sm' ? 11 : 12;
  const iconSize = size === 'sm' ? 12 : 14;

  return React.createElement(
    'span',
    {
      'data-testid': 'cda-risk-indicator',
      'data-risk': level,
      className: className || undefined,
      title: `Inherent risk: ${label}`,
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: size === 'sm' ? '1px 7px' : '2px 8px',
        borderRadius: 'var(--border-radius-sm, 6px)',
        fontSize,
        fontWeight: 500,
        lineHeight: 1.4,
        color,
        background: softFill(color, 10),
      },
    },
    React.createElement(CdaIcon, { name: ICON[level], size: iconSize, color }),
    React.createElement('span', null, label),
  );
}
